import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Menu, X, Moon, Sun, User, LogOut, LayoutDashboard, PlaneTakeoff } from "lucide-react";
import { Button } from "@/components/ui/button";
import CurrencySelector from "@/components/site/CurrencySelector";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/packages", label: "Packages" },
  { to: "/bundles", label: "Bundles" },
  { to: "/destinations", label: "Destinations" },
  { to: "/group-tours", label: "Group Tours" },
  { to: "/honeymoon", label: "Honeymoon" },
  { to: "/corporate", label: "Corporate" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", on);
    on();
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 ${scrolled ? "glass shadow-lg" : "bg-transparent"}`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
          <span className="w-11 h-11 rounded-2xl flex items-center justify-center btn-gold">
            <PlaneTakeoff className="w-6 h-6" />
          </span>
          <div className="leading-tight">
            <div className="font-[Playfair_Display] text-xl font-bold text-blue-950 dark:text-white">Your Trips</div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#D4AF37] font-semibold">Pack &amp; Go</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-medium ${
                  isActive
                    ? "text-[#D4AF37]"
                    : "text-slate-700 dark:text-slate-200 hover:text-[#D4AF37]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <CurrencySelector />
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="w-10 h-10 rounded-full flex items-center justify-center glass hover:scale-105"
          >
            {theme === "dark" ? <Sun className="w-4 h-4 text-[#D4AF37]" /> : <Moon className="w-4 h-4 text-blue-950" />}
          </button>

          {user ? (
            <div className="relative">
              <button
                data-testid="user-menu-toggle"
                onClick={() => setUserMenu((v) => !v)}
                className="w-10 h-10 rounded-full flex items-center justify-center btn-gold"
              >
                <User className="w-4 h-4" />
              </button>
              {userMenu && (
                <div className="absolute right-0 top-12 w-56 rounded-2xl glass p-2 shadow-2xl">
                  <div className="px-3 py-2 text-xs uppercase tracking-widest text-slate-500">{user.name}</div>
                  <Link to="/account" onClick={() => setUserMenu(false)} data-testid="menu-account"
                    className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#D4AF37]/10">
                    <User className="w-4 h-4" /> My Account
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setUserMenu(false)} data-testid="menu-admin"
                      className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#D4AF37]/10">
                      <LayoutDashboard className="w-4 h-4" /> Admin Panel
                    </Link>
                  )}
                  <button
                    data-testid="menu-logout"
                    onClick={async () => { await logout(); setUserMenu(false); nav("/"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-red-500/10 text-red-600"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" data-testid="nav-signin" className="hidden sm:inline-flex text-sm font-semibold px-4 py-2 rounded-full btn-outline-gold">
              Sign In
            </Link>
          )}

          <Link to="/packages" data-testid="nav-book-now" className="hidden sm:inline-flex text-sm font-semibold px-5 py-2 rounded-full btn-gold">
            Book Now
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden w-10 h-10 rounded-full flex items-center justify-center glass"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden glass border-t border-white/20 px-6 py-4 grid grid-cols-2 gap-2">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
              className={({ isActive }) => `px-3 py-2 rounded-xl text-sm ${isActive ? "text-[#D4AF37]" : "text-slate-700 dark:text-slate-200"}`}>
              {n.label}
            </NavLink>
          ))}
          {!user && (
            <Link to="/login" onClick={() => setOpen(false)} className="col-span-2 text-center btn-gold rounded-xl py-2 mt-2">Sign In</Link>
          )}
        </div>
      )}
    </header>
  );
}
